/* ==========================================================================
   Funnel Relay — Section Kit runtime (fr-kit.js)
   Layer: UNIVERSAL. AJAX add-to-cart intercept for ATC forms marked
   `data-fr-cart-add` (rendered by snippets/fr-cta-button.liquid).

   ---------------------------------------------------------------------------
   Move 3 Bounce 4.6 rework — Dawn-compatible cart-drawer trigger.
   ---------------------------------------------------------------------------
   The original Move 2 implementation added the item, then dispatched generic
   cart events (cart:refresh / cart:open / theme:cart:open / cart:added) on
   document.body. Dawn-family themes — the dominant Shopify base, and Daybreak's
   base — listen for NONE of those. Dawn's <cart-drawer> opens ONLY via direct
   method calls: its own assets/product-form.js POSTs /cart/add with a
   `sections` param, then calls `cart.renderContents(response)`, which re-renders
   the drawer's sections and calls `open()`. Because the Move 2 kit bypassed
   Dawn's product-form entirely, the item was added but the drawer never opened
   and there was zero feedback — read at Gate 4 as a "dead" ATC button on
   desktop (Bug 1) and a never-opening drawer everywhere (Bug 2).

   This rework mirrors Dawn's assets/product-form.js flow as the PRIMARY path,
   keeps the Move 2 generic-event dispatch as a fallback for non-Dawn themes,
   and native form submit as the last resort.

   Post-add priority:
     PATH 1 — Dawn drawer (primary):  a <cart-drawer>/<cart-notification> element
                                      exposing renderContents() exists → POST
                                      /cart/add.js with `sections` (pulled live
                                      from the drawer via getSectionsToRender(),
                                      else FR_CART_SECTIONS) + `sections_url`,
                                      then cart.renderContents(json) — re-renders
                                      + opens the drawer. Mirrors Dawn's
                                      assets/product-form.js onSubmitHandler.
                                      Bounce 4.7: also clears `is-empty` on the
                                      <cart-drawer> element after renderContents
                                      (Dawn does this in its .finally) so the
                                      first add-from-empty renders visibly.
     PATH 2 — event bus (fallback):   no Dawn drawer → POST /cart/add.js WITHOUT
                                      sections → dispatch cart:refresh / cart:open
                                      / theme:cart:open / cart:added on
                                      document.body (unchanged Move 2 behavior).
     PATH 3 — native submit (last):   any fetch failure in Path 1 or 2 →
                                      form.submit() (browser posts the form and
                                      redirects to the /cart page).

   Loaded with `defer`, so the DOM is already parsed when this runs — no
   DOMContentLoaded wait is needed. The snippet renders this <script> once per
   ATC call site; the __frKitInit guard makes every run after the first a no-op.
   ========================================================================== */
/* ==========================================================================
   MODULE MANIFEST (Item 17) — this file ships as ONE asset with independently
   guarded modules. Current modules:
     0. FRK namespace         — guard window.FRK                 — infrastructure, no DOM
     1. ATC cart intercept    — guard window.__frKitInit         — hooks form[data-fr-cart-add]
     2. Slider (shared)       — guard window.__frKitSliderInit   — hooks [data-fr-slider]
        Generic: used by fr-reviews (Item AA) AND fr-checklist slider (Item 14) with
        no per-section code — any section emitting the [data-fr-slider] markup gets it.
     3. Disclosure (Item 15)  — FRK.register('disclosure')       — hooks [data-fr-disclosure]
        Applies the ENTIRE ARIA role layer for fr-disclosure (tabs or accordion) and
        owns the tabs->accordion swap at 750px. The section emits no roles at all.
   Loaded once (deduped) by any section that needs it (ATC snippet, or a slider /
   disclosure section's own <script> tag); the per-module guards make every extra
   load a no-op.

   SPLIT TRIGGER (decided Mega-Batch 1) — FIRED, resolved Mega-Batch 2 as Option C.
   The decision was: when a THIRD independent capability lands, adopt a light in-file
   namespace (window.FRK.register(name, initFn) run on DOM-ready) rather than splitting
   into fr-kit-<module>.js. Module 3 is that third capability, so the namespace ships
   here as module 0 and module 3 registers through it.

   Modules 1 and 2 deliberately still SELF-INVOKE behind their original guards and are
   byte-unchanged. Rewrapping them was rejected: both are validated on shipped builds
   (module 1 carries the whole Bounce 4.5/4.6/4.7 Dawn-drawer history and runs on every
   ATC on every brand), so a refactor's blast radius would dwarf its test set — the
   exercise-scope-drift failure mode. They migrate OPPORTUNISTICALLY: the next time
   either is edited for its own reasons, rewrap it as FRK.register at that point.

   Still single-file. Split into fr-kit-<module>.js only if per-page JS payload becomes
   a real concern; multiple files mean more <script>/asset_url loads.
   ========================================================================== */

/* ==========================================================================
   Funnel Relay — Section Kit runtime: FRK NAMESPACE (fr-kit.js, module 0)
   Infrastructure only — touches no DOM of its own. Owns two things every kit
   module otherwise reimplements: the DOM-ready dispatch and the once-only
   registration guard. A module registering a name already registered is a
   no-op, which is what makes a double-load of this asset harmless.

   Each module's init runs inside a try/catch so one module throwing cannot
   take the others down with it — a page can carry an ATC, a slider and a
   disclosure at once, and they must fail independently.
   ========================================================================== */
(function () {
  if (window.FRK) return;

  var registered = {};

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  window.FRK = {
    registered: registered,
    register: function (name, initFn) {
      if (registered[name]) return false;   // per-module guard, same contract as __frKit*Init
      registered[name] = true;
      onReady(function () {
        try {
          initFn();
        } catch (err) {
          console.error('[fr-kit] module "' + name + '" init failed:', err);
        }
      });
      return true;
    }
  };
})();
(function () {
  if (window.__frKitInit) return;
  window.__frKitInit = true;

  /* Section names Dawn's cart drawer re-renders on add. Resolved LIVE from the
     theme at runtime via cart.getSectionsToRender() when available (so it tracks
     any theme customisation); this array is only the fallback default — Dawn's
     stock cart-drawer sections. A future brand whose theme differs can override
     this one config array. */
  var FR_CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  // Capture phase: intercept the submit before any host-theme handler can act.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-fr-cart-add]')) return;

    event.preventDefault();

    // PATH 1 — Dawn-family drawer (primary). Dawn's own product-form.js resolves
    // its cart element as (cart-notification || cart-drawer); we accept either,
    // provided it exposes the renderContents() contract.
    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (cart && typeof cart.renderContents === 'function') {
      frDawnDrawerAdd(form, cart);
      return;
    }

    // PATH 2 — generic event-bus fallback (unchanged Move 2 behavior).
    frGenericEventAdd(form);
  }, true);

  /* PATH 1 — mirror Dawn assets/product-form.js: FormData body + `sections` +
     `sections_url`, no explicit Content-Type (FormData sets its own multipart
     boundary), then cart.renderContents(json) to re-render + open the drawer. */
  function frDawnDrawerAdd(form, cart) {
    var sections = (typeof cart.getSectionsToRender === 'function')
      ? cart.getSectionsToRender().map(function (s) { return s.id; })
      : FR_CART_SECTIONS;

    var body = new FormData(form);
    body.append('sections', sections.join(','));
    body.append('sections_url', window.location.pathname);
    if (typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(document.activeElement);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add.js (Dawn path) returned ' + response.status);
        return response.json();
      })
      .then(function (state) {
        if (state && state.status) {
          throw new Error('cart/add.js (Dawn path) add error: ' + (state.message || state.status));
        }
        cart.renderContents(state); // re-renders drawer sections and calls open()
        // Bounce 4.7: clear `is-empty` on the <cart-drawer> ELEMENT itself. Dawn's own
        // product-form.js does this in its .finally() — renderContents() only clears
        // is-empty on the child `.drawer__inner`, NOT on the element. Without this, the
        // empty-state CSS (`cart-drawer.is-empty .drawer__inner` grid layout +
        // `cart-drawer.is-empty .drawer__header { display:none }`) keeps a freshly-added
        // item styled-as-empty (invisible) until the drawer is re-opened. Success-path
        // only (inside .then, not .finally) so we never clear it when renderContents threw.
        if (cart && cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
        console.debug('fr-kit: Dawn drawer path fired; sections=', sections.join(','));
      })
      .catch(function (err) {
        console.error('[fr-kit] Dawn drawer add failed:', err);
        frNativeSubmit(form);
      });
  }

  /* PATH 2 — generic event dispatch (verbatim Move 2 behavior): POST
     /cart/add.js with no sections, then fire the four cart events on
     document.body for any host theme that listens for them. */
  function frGenericEventAdd(form) {
    var params = new URLSearchParams(new FormData(form));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: params.toString()
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add.js returned ' + response.status);
        return response.json();
      })
      .then(function () {
        // Different host themes listen for different cart-drawer event names.
        ['cart:refresh', 'cart:open', 'theme:cart:open', 'cart:added'].forEach(function (name) {
          document.body.dispatchEvent(new CustomEvent(name, { bubbles: true }));
        });
        console.debug('fr-kit: generic event dispatch path fired');
      })
      .catch(function (err) {
        console.error('[fr-kit] AJAX cart add failed:', err);
        frNativeSubmit(form);
      });
  }

  /* PATH 3 — native form submit (verbatim Move 2 fallback). form.submit() does
     NOT fire a submit event, so this cannot loop back into the listener. */
  function frNativeSubmit(form) {
    console.debug('fr-kit: native submit fallback fired');
    form.submit();
  }
})();

/* ==========================================================================
   Funnel Relay — Section Kit runtime: SLIDER (fr-kit.js, module 2)
   Progressive enhancement for opt-in `.fr-slider` tracks (fr-reviews slider
   layout, and any future card set). The core scroll-snap track works with NO
   JS (native touch-swipe on mobile); this module reveals + wires prev/next
   arrows and dot indicators, which are rendered `hidden` so a no-JS page shows
   a clean bare scroll track rather than dead controls. Idempotent via its own
   guard, and safe to load alongside module 1 (each guards independently).
   ========================================================================== */
(function () {
  if (window.__frKitSliderInit) return;
  window.__frKitSliderInit = true;

  function trackGap(track) {
    var cs = getComputedStyle(track);
    return parseFloat(cs.columnGap || cs.gap || '0') || 0;
  }

  function initSlider(root) {
    var track = root.querySelector('[data-fr-slider-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) return; // nothing to slide

    var prev = root.querySelector('[data-fr-slider-prev]');
    var next = root.querySelector('[data-fr-slider-next]');
    var dotsWrap = root.querySelector('[data-fr-slider-dots]');

    // Reveal controls now that JS is running (they ship hidden for the no-JS case).
    [prev, next, dotsWrap].forEach(function (el) { if (el) el.hidden = false; });

    function perView() {
      var sw = slides[0].getBoundingClientRect().width + trackGap(track);
      return Math.max(1, Math.round(track.clientWidth / sw));
    }
    function pageCount() { return Math.max(1, Math.ceil(slides.length / perView())); }
    function currentPage() {
      return Math.min(pageCount() - 1, Math.round(track.scrollLeft / (track.clientWidth || 1)));
    }

    var dots = [];
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      dots = [];
      var pc = pageCount();
      for (var i = 0; i < pc; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'fr-slider__dot';
        b.setAttribute('aria-label', 'Go to review group ' + (i + 1));
        (function (idx) {
          b.addEventListener('click', function () {
            track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
          });
        })(i);
        dotsWrap.appendChild(b);
        dots.push(b);
      }
    }

    function update() {
      var pc = pageCount();
      var cur = currentPage();
      dots.forEach(function (d, i) { d.setAttribute('aria-current', i === cur ? 'true' : 'false'); });
      if (prev) prev.disabled = cur <= 0;
      if (next) next.disabled = cur >= pc - 1;
    }

    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
    });
    var raf;
    track.addEventListener('scroll', function () {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(update);
    });
    var rt;
    window.addEventListener('resize', function () {
      window.clearTimeout(rt);
      rt = window.setTimeout(function () { buildDots(); update(); }, 150);
    });

    buildDots();
    update();
  }

  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('[data-fr-slider]')).forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();

/* ==========================================================================
   Funnel Relay — Section Kit runtime: DISCLOSURE (fr-kit.js, module 3)
   Item 15 / Mega-Batch 2. Hooks [data-fr-disclosure] emitted by
   sections/fr-disclosure.liquid and applies the ENTIRE ARIA role layer.

   The section emits NO roles — not tablist/tab/tabpanel, not even
   aria-expanded="false". It ships plain content with every panel visible and
   every trigger aria-expanded="true" (truthful: nothing is collapsed yet).
   This module's first act is to collapse and project the semantics.

   Two projections over one DOM:
     TABS      (layout=tabs, >=750px) — APG Tabs: role=tablist/tab/tabpanel,
               roving tabindex, arrow/Home/End with AUTOMATIC activation
               (panels are already-parsed static DOM, so APG's cheap-panel case
               applies), aria-expanded removed (aria-selected carries state).
     ACCORDION (layout=accordion at all widths; layout=tabs below 750px) —
               APG Accordion: trigger stays a <button> inside its <h3>,
               aria-expanded toggled, MULTI-SELECT (opening one never closes
               another — "one at a time" is what layout=tabs already means, so
               a single-select accordion would leave no way to get independent
               toggles at all). No arrow-key handling: APG lists it as optional
               for accordions and native Tab order is the expected model.

   WHY JS OWNS THE SWAP: CSS cannot rewrite an ARIA role. A media-query-only
   collapse would leave role="tab" markup rendering as an accordion on every
   phone. The role IS the switch — fr-disclosure's CSS keys its tabs
   presentation off [role="tablist"], so the visual and semantic modes cannot
   disagree, and a JS failure lands on accordion styling that matches the
   all-visible static fallback.

   Two APG deviations, both deliberate:
     - role="region" on accordion panels only when there are <=6 items. APG
       notes a landmark per panel makes a screen reader's landmark list useless
       past a handful; aria-labelledby (static) is what actually ties panel to
       trigger.
     - tabindex="0" on a tabpanel only when it holds NO focusable element.
       APG's rule is conditional; a richtext body may or may not contain a link,
       and an unconditional tabindex would add a redundant tab stop in front of
       that link.
   ========================================================================== */
window.FRK.register('disclosure', function () {
  var DESKTOP_MQ = '(min-width: 750px)';
  var REGION_MAX = 6;          // above this, role="region" per panel is landmark spam
  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  function slice(nodeList) { return Array.prototype.slice.call(nodeList); }

  function initRoot(root) {
    var layout = root.getAttribute('data-fr-disclosure') === 'tabs' ? 'tabs' : 'accordion';
    var firstOpen = root.getAttribute('data-fr-disclosure-first-open') === 'true';
    var label = root.getAttribute('data-fr-disclosure-label');

    var items = slice(root.querySelectorAll('[data-fr-disclosure-item]'));
    if (!items.length) return;

    var headings = [], triggers = [], panels = [];
    var i;
    for (i = 0; i < items.length; i++) {
      var trigger = items[i].querySelector('[data-fr-disclosure-trigger]');
      var panel = items[i].querySelector('[data-fr-disclosure-panel]');
      // Incomplete pair -> bail out entirely and leave the section fully visible.
      // A half-applied role layer is worse than no enhancement at all.
      if (!trigger || !panel) return;
      triggers.push(trigger);
      panels.push(panel);
      headings.push(items[i].querySelector('.fr-disclosure__heading') || trigger.parentNode);
    }

    var count = items.length;
    var useRegion = count <= REGION_MAX;
    var mode = null;
    var active = 0;              // tabs: the selected index
    var open = [];               // accordion: per-item open state (multi-select)

    // Seed. layout=tabs forces item 1 open regardless of first_open, so a mobile
    // first-load of a tabs section matches what resizing down from desktop gives.
    for (i = 0; i < count; i++) {
      open[i] = (i === 0) && (layout === 'tabs' ? true : firstOpen);
    }

    function syncPanelTabindex(panel) {
      if (panel.querySelector(FOCUSABLE)) panel.removeAttribute('tabindex');
      else panel.setAttribute('tabindex', '0');
    }

    function applyTabs() {
      root.setAttribute('role', 'tablist');
      root.setAttribute('aria-orientation', 'horizontal');
      root.setAttribute('aria-label', label || 'Section tabs');
      for (var j = 0; j < count; j++) {
        // The <h3> is right for an accordion but would inject a spurious heading
        // between tablist and tab.
        headings[j].setAttribute('role', 'presentation');
        triggers[j].setAttribute('role', 'tab');
        triggers[j].setAttribute('aria-selected', j === active ? 'true' : 'false');
        triggers[j].setAttribute('tabindex', j === active ? '0' : '-1');
        triggers[j].removeAttribute('aria-expanded');
        panels[j].setAttribute('role', 'tabpanel');
        panels[j].hidden = (j !== active);
        syncPanelTabindex(panels[j]);
      }
    }

    function applyAccordion() {
      root.removeAttribute('role');
      root.removeAttribute('aria-orientation');
      root.removeAttribute('aria-label');
      for (var j = 0; j < count; j++) {
        headings[j].removeAttribute('role');
        triggers[j].removeAttribute('role');
        triggers[j].removeAttribute('aria-selected');
        triggers[j].removeAttribute('tabindex');
        triggers[j].setAttribute('aria-expanded', open[j] ? 'true' : 'false');
        if (useRegion) panels[j].setAttribute('role', 'region');
        else panels[j].removeAttribute('role');
        panels[j].removeAttribute('tabindex');
        panels[j].hidden = !open[j];
      }
    }

    function selectTab(index, moveFocus) {
      active = index;
      for (var j = 0; j < count; j++) {
        triggers[j].setAttribute('aria-selected', j === index ? 'true' : 'false');
        triggers[j].setAttribute('tabindex', j === index ? '0' : '-1');
        panels[j].hidden = (j !== index);
      }
      if (moveFocus) triggers[index].focus();
    }

    function toggleItem(index) {
      open[index] = !open[index];
      triggers[index].setAttribute('aria-expanded', open[index] ? 'true' : 'false');
      panels[index].hidden = !open[index];
    }

    function setMode(next) {
      if (mode === next) return;

      // Hold focus across the swap — losing it to <body> mid-interaction is a
      // real regression, even though the swap only fires on rotate/resize.
      var focusedIndex = root.contains(document.activeElement)
        ? triggers.indexOf(document.activeElement)
        : -1;

      if (next === 'tabs') {
        // accordion -> tabs: a tablist admits exactly one selection, and the
        // accordion is multi-select, so first-open-wins is the only
        // deterministic rule. Nothing open -> item 1.
        var firstOpenIndex = open.indexOf(true);
        active = firstOpenIndex === -1 ? 0 : firstOpenIndex;
        applyTabs();
      } else {
        // tabs -> accordion: the active tab becomes the ONE open item. Opening
        // them all would dump the whole content wall on the smallest viewport,
        // which is the opposite of what the collapse is for. Guarded on the
        // previous mode so a first init never clobbers the first_open seed.
        if (mode === 'tabs') {
          for (var j = 0; j < count; j++) open[j] = (j === active);
        }
        applyAccordion();
      }
      mode = next;

      if (focusedIndex !== -1) {
        triggers[focusedIndex].focus();
        // FOCUS-SYNC OVERRIDE. Going accordion -> tabs, focus may sit on a trigger
        // OTHER than the open item (in an accordion, focus roams freely; in a
        // tablist under automatic activation it cannot). Leaving it would produce a
        // focused-but-unselected tab carrying tabindex="-1" and aria-selected="false"
        // — precisely the state APG's automatic-activation model says must not
        // persist. So the focused trigger wins over first-open-wins here. On the
        // ordinary path this is a no-op: a real click focuses the tab it activates.
        if (next === 'tabs' && focusedIndex !== active) selectTab(focusedIndex, false);
      }
    }

    // Handlers bind ONCE and branch on the current mode — rebinding per mode is
    // how you end up with duplicate listeners after a few resizes.
    for (i = 0; i < count; i++) {
      (function (index) {
        triggers[index].addEventListener('click', function () {
          if (mode === 'tabs') selectTab(index, false);
          else toggleItem(index);
        });
        triggers[index].addEventListener('keydown', function (event) {
          if (mode !== 'tabs') return;   // accordion Enter/Space is native <button> behavior
          var next = -1;
          if (event.key === 'ArrowRight') next = (index + 1) % count;
          else if (event.key === 'ArrowLeft') next = (index - 1 + count) % count;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = count - 1;
          else return;
          event.preventDefault();
          selectTab(next, true);   // automatic activation: selection follows focus
        });
      })(i);
    }

    if (layout === 'tabs') {
      var mql = window.matchMedia(DESKTOP_MQ);
      var sync = function () { setMode(mql.matches ? 'tabs' : 'accordion'); };
      // matchMedia fires only on threshold crossings, so no debounce is needed
      // (unlike the slider's resize handler).
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', sync);
      else if (typeof mql.addListener === 'function') mql.addListener(sync);
      sync();
    } else {
      setMode('accordion');
    }
  }

  slice(document.querySelectorAll('[data-fr-disclosure]')).forEach(initRoot);
});
